import { defineStore } from 'pinia';
import _ from 'lodash';
import {
  IConversation,
  IConversationMessage,
  IConversationItemDirectList,
  IConversationItemGroupList,
  IConversationItemDirectId,
  IConversationItemGroupId,
  IGroup,
  ISpacesContact,
  ICase,
  IMedia,
  IMockMessages,
} from '@/types/interfaces';
// eslint-disable-next-line import/no-cycle
import MessagesService from '../services/MessagesService';
// eslint-disable-next-line import/no-cycle
import SpacesService from '@/modules/spaces/services/SpacesService';
import { CONTACT_STATUS, MESSAGE_ATTACHMENT_TYPE, MOCK_MESSAGE_STATUS } from '@/types/enums';
// eslint-disable-next-line import/no-cycle
import { useSpacesStore } from '@/modules/spaces/store/spacesStore';

export type MessagesState = {
  search: string,
  conversations: (IConversationItemDirectList | IConversationItemGroupList)[],
  // groupConversations: IConversationItemGroupList[],
  currentConversation: IConversation | null,
  groupsForChat: IGroup[]
  mockMessages: IMockMessages[];
  toBeSync: boolean;
}

export const useMessagesStore = defineStore({
  id: 'messagesStore',

  state: () => ({
    search: '',
    partner: null,
    conversations: [],
    // groupConversations: [],
    currentConversation: null,
    groupsForChat: [],
    mockMessages: [],
    toBeSync: false,
  } as MessagesState),

  getters: {
    filteredConversations(): (IConversationItemDirectList | IConversationItemGroupList)[] {
      const spacesStore = useSpacesStore();
      return this.conversations.filter((el: any) => {
        if (el?.partner) {
          const fullName = (el as IConversationItemDirectList).partner.firstName + (el as IConversationItemDirectList).partner.lastName;
          return fullName.toLowerCase().includes(spacesStore.search.toLowerCase());
        }
        return (el as IConversationItemGroupList).group.name.toLowerCase().includes(spacesStore.search.toLowerCase());
      });
    },
    filteredByNameGroupsChat(): IGroup[] {
      const spacesStore = useSpacesStore();
      return this.groupsForChat.filter(
        (group) => group.name.toLocaleLowerCase().includes(spacesStore.search.toLocaleLowerCase()),
      );
    },
  },

  actions: {
    async fetchConversations(): Promise<(IConversationItemDirectList | IConversationItemGroupList)[]> {
      return MessagesService.fetchLastConverations().then((response) => {
        if (response.success) {
          this.conversations = response.results.conversations;
        }
        return this.conversations;
      });
    },

    // async fetchGroupConversations(): Promise<IConversationItemGroupList[]> {
    //   return MessagesService.fetchGroupConverations().then((response) => {
    //     if (response.success) {
    //       this.groupConversations = response.results.conversations;
    //     }
    //     return this.groupConversations;
    //   });
    // },

    async fetchGroupsForChat() {
      return SpacesService.fetchAllGroups().then((response) => {
        this.groupsForChat = response.results.data;
      });
    },

    conversationGroupListToConversation(convItem: IConversationItemGroupList): IConversation {
      return {
        contactStatus: CONTACT_STATUS.NO_CONTACT,
        conversationId: convItem.conversationId,
        messages: [],
        info: {
          id: convItem.group.id,
          name: convItem.group.name,
          photo: convItem.group.photo,
          isGroup: true,
        },
        previousMessageTimestamp: null,
        systemMessage: null,
        group: convItem.group,
      };
    },

    conversationItemToConversation(convItem: IConversationItemDirectId | IConversationItemGroupId): IConversation {
      function getInfo(item: IConversationItemDirectId | IConversationItemGroupId) {
        if ((item as IConversationItemDirectId)?.partner) {
          return {
            id: (item as IConversationItemDirectId).partner.accountId,
            name: `${(item as IConversationItemDirectId).partner.firstName} ${(item as IConversationItemDirectId).partner.lastName}`,
            photo: (item as IConversationItemDirectId).partner.photo,
            isGroup: false,
          };
        }

        return {
          id: (item as IConversationItemGroupId).group.id,
          name: (item as IConversationItemGroupId).group.name,
          photo: (item as IConversationItemGroupId).group.photo,
          isGroup: true,
        };
      }

      return {
        contactStatus: convItem.contactStatus,
        conversationId: convItem.conversationId,
        messages: convItem.messages,
        info: getInfo(convItem),
        previousMessageTimestamp: convItem.previousMessageTimestamp,
        systemMessage: null,
        group: (convItem as IConversationItemGroupId)?.group,
        allowedToWrite: convItem.allowedToWrite,
      };
    },

    async updateContactStatusForCurrentConversation() {
      if (this.currentConversation?.info?.id && !this.currentConversation.info.isGroup) {
        const timeStamp = this.currentConversation!.previousMessageTimestamp || '0';
        MessagesService.fetchConversationById(this.currentConversation?.info?.id, timeStamp, 0).then((response) => {
          if (response.success && this.currentConversation) {
            this.currentConversation.contactStatus = response.results.conversation.contactStatus;
          }
        });
      }
    },

    async fetchConversationById(userId: string, amount = 20, loadMore?: boolean): Promise<IConversation | null> {
      const getLastTimestamp = (): string => {
        const currentConv = this.conversations.find((conv) => (conv as IConversationItemDirectList)?.partner?.accountId === userId);
        if (currentConv) {
          return currentConv.lastMessageTimestamp || '0';
        }
        return '0';
      };

      const timeStamp = loadMore ? this.currentConversation!.previousMessageTimestamp! : getLastTimestamp();

      return MessagesService.fetchConversationById(userId, timeStamp, amount)
        .then((response) => {
          if (response.success) {
            if (loadMore) {
              const currentConv = { ...this.currentConversation! };
              currentConv.messages = [...response.results.conversation.messages, ...this.currentConversation!.messages];
              currentConv.previousMessageTimestamp = response.results.conversation.previousMessageTimestamp;
              this.currentConversation = currentConv;
            } else {
              this.currentConversation = this.conversationItemToConversation(response.results.conversation);
            }
          }

          return this.currentConversation;
        });
    },

    async fetchGroupConversationById(userId: string, amount = 20, loadMore?: boolean): Promise<IConversation | null> {
      const getLastTimestamp = (): string => {
        const currentConv = this.conversations.find((conv) => (conv as IConversationItemGroupList)?.group?.id === userId);
        if (currentConv) {
          return currentConv.lastMessageTimestamp || '0';
        }
        return '0';
      };

      const timeStamp = loadMore ? this.currentConversation!.previousMessageTimestamp! : getLastTimestamp();

      return MessagesService.fetchGroupConversationById(userId, timeStamp, amount)
        .then((response) => {
          if (response.success) {
            if (loadMore) {
              const currentConv = { ...this.currentConversation! };
              currentConv.messages = [...response.results.conversation.messages, ...this.currentConversation!.messages];
              currentConv.previousMessageTimestamp = response.results.conversation.previousMessageTimestamp;
              this.currentConversation = currentConv;
            } else {
              this.currentConversation = this.conversationItemToConversation(response.results.conversation);
            }
          }

          return this.currentConversation;
        });
    },

    addMessageToConversation(msg: IConversationMessage): void {
      // console.log('messageStore MSG ids', msg.conversationId, this.currentConversation?.conversationId, msg.conversationId === this.currentConversation?.conversationId);
      // console.log('messageStore MSG', msg, msg.conversationId === this.currentConversation?.conversationId);
      if (msg.conversationId === this.currentConversation?.conversationId) {
        this.currentConversation!.messages.push(msg);
        // this.currentConversation.previousMessageTimestamp = msg.created;
      }

      const conversation = this.conversations.find((conv) => conv.conversationId === msg.conversationId);
      if (conversation) {
        if (this.currentConversation?.info.id !== conversation.conversationId) {
          conversation.unreadCount += 1;
        }
        if (msg.content) {
          conversation.previewText = msg.content;
        } else if (msg.attachmentType === MESSAGE_ATTACHMENT_TYPE.CASE) {
          conversation.previewText = `${msg.attachmentType}: ${(msg.attachment as ICase).title} is shared`;
        } else if (msg.attachmentType === MESSAGE_ATTACHMENT_TYPE.MEDIA) {
          conversation.previewText = `${msg.attachmentType}: ${(msg.attachment as IMedia).name} is shared`;
        }
        conversation.lastMessageTimestamp = msg.created;
        this.conversations = _.orderBy(this.conversations, [(item) => item.lastMessageTimestamp], ['desc']);
      } else {
        this.fetchConversations();
      }
    },

    addMockMessage(contactId: string, text: string, uuid: string): void {
      const mockMsg = {
        text,
        uuid,
        status: MOCK_MESSAGE_STATUS.SENDING,
      };

      const msgObj = this.mockMessages.find((mock) => mock.key === contactId);

      if (msgObj) {
        msgObj.value.push(mockMsg);
      } else {
        this.mockMessages.push({
          key: contactId, value: [mockMsg],
        });
      }
    },

    removeMockMessage(contactId: string, uuid: string) {
      const msgObj = this.mockMessages.find((mock) => mock.key === contactId);

      if (msgObj) {
        msgObj.value = msgObj.value.filter((msg) => msg.uuid !== uuid);
      }
    },

    setMockMessageError() {
      this.mockMessages = this.mockMessages
        .map((mock) => (
          {
            ...mock,
            value: mock.value.map((val) => ({ ...val, status: MOCK_MESSAGE_STATUS.ERROR })),
          }
        ));
    },
  },

});
